/**
 * Provides snapping functionality for draggable canvas elements.
 * Handles snapping to other elements' edges and to canvas centerlines.
 */
export default class SnapHelper {
  /** Accounts for element outline (2px) + outline-offset (2px) */
  static VISUAL_BOUNDARY_OFFSET = 4;

  /** Maximum distance in pixels for snap to activate */
  static SNAP_THRESHOLD = 6;

  /**
   * @param {HTMLElement} canvasElement - The canvas container element
   */
  constructor(canvasElement) {
    this.canvasWidth = canvasElement.scrollWidth;
    this.canvasHeight = canvasElement.scrollHeight;
    this.canvasCenterX = this.canvasWidth / 2;
    this.canvasCenterY = this.canvasHeight / 2;
    this.snapTargets = [];
  }

  /**
   * Builds snap targets from element boundaries, accounting for visual offsets.
   * Creates left/right/top/bottom snap points for each non-excluded element.
   *
   * @param {Array<HTMLElement>} allElements - All elements to consider for snapping
   * @param {Set<HTMLElement>} excludedElements - Elements to exclude (e.g., currently dragged element)
   */
  buildSnapTargets(allElements, excludedElements) {
    this.snapTargets = [];
    const offset = SnapHelper.VISUAL_BOUNDARY_OFFSET;

    allElements.forEach(el => {
      if (excludedElements.has(el)) return;

      const left = el.offsetLeft;
      const top = el.offsetTop;
      const width = el.offsetWidth;
      const height = el.offsetHeight;

      this.snapTargets.push(
        { position: left - offset, type: 'left', axis: 'horizontal', sourceElement: el },
        { position: left + width + offset, type: 'right', axis: 'horizontal', sourceElement: el },
        { position: top - offset, type: 'top', axis: 'vertical', sourceElement: el },
        { position: top + height + offset, type: 'bottom', axis: 'vertical', sourceElement: el }
      );
    });
  }

  clearSnapTargets() {
    this.snapTargets = [];
  }

  /**
   * Calculates snapped position for an element being dragged.
   * Attempts to snap to both element edges and canvas centerlines.
   *
   * @param {number} left - Current left position of the element
   * @param {number} top - Current top position of the element
   * @param {HTMLElement} element - The element being dragged
   * @returns {{left: number, top: number, snappedHorizontal: Object|null, snappedVertical: Object|null}}
   */
  snap(left, top, element) {
    const width = element.offsetWidth;
    const height = element.offsetHeight;

    const horizontalResult = this._snapAxis(left, width, 'horizontal');
    const verticalResult = this._snapAxis(top, height, 'vertical');

    return {
      left: horizontalResult.position,
      top: verticalResult.position,
      snappedHorizontal: horizontalResult.target,
      snappedVertical: verticalResult.target
    };
  }

  /** Finds snap target for a single axis, checking element edges, then canvas edges, then centerlines. */
  _snapAxis(position, size, axis) {
    const draggedPoints = this._buildDraggedPoints(size, axis);

    const elementSnap = this._findElementSnap(position, draggedPoints, axis);
    if (elementSnap) return elementSnap;

    const canvasEdgeSnap = this._findCanvasEdgeSnap(position, size, axis);
    if (canvasEdgeSnap) return canvasEdgeSnap;

    const centerlineSnap = this._findCenterlineSnap(position, size, axis);
    if (centerlineSnap) return centerlineSnap;

    return { position, target: null };
  }

  _buildDraggedPoints(size, axis) {
    const offset = SnapHelper.VISUAL_BOUNDARY_OFFSET;
    const [startType, endType] = axis === 'horizontal' ? ['left', 'right'] : ['top', 'bottom'];

    return [
      { offset: -offset, type: startType },
      { offset: size + offset, type: endType }
    ];
  }

  _findElementSnap(position, draggedPoints, axis) {
    for (const target of this.snapTargets) {
      if (target.axis !== axis) continue;

      for (const point of draggedPoints) {
        const draggedPosition = position + point.offset;
        if (Math.abs(draggedPosition - target.position) < SnapHelper.SNAP_THRESHOLD) {
          return {
            position: target.position - point.offset,
            target
          };
        }
      }
    }

    return null;
  }

  /** Checks if element edges are within snap threshold of canvas edges. */
  _findCanvasEdgeSnap(position, size, axis) {
    const offset = SnapHelper.VISUAL_BOUNDARY_OFFSET;
    const isHorizontal = axis === 'horizontal';
    const canvasSize = isHorizontal ? this.canvasWidth : this.canvasHeight;

    const elementStart = position - offset;
    const elementEnd = position + size + offset;

    // Check start edge (left or top)
    if (Math.abs(elementStart) < SnapHelper.SNAP_THRESHOLD) {
      return {
        position: offset,
        target: { position: 0, type: 'canvas-edge', axis }
      };
    }

    // Check end edge (right or bottom)
    if (Math.abs(elementEnd - canvasSize) < SnapHelper.SNAP_THRESHOLD) {
      return {
        position: canvasSize - size - offset,
        target: { position: canvasSize, type: 'canvas-edge', axis }
      };
    }

    return null;
  }

  _findCenterlineSnap(position, size, axis) {
    const isHorizontal = axis === 'horizontal';
    const result = isHorizontal
      ? this._checkCanvasCenterlineHorizontal(position, size)
      : this._checkCanvasCenterlineVertical(position, size);

    if (result !== null) {
      const centerPosition = isHorizontal ? this.canvasCenterX : this.canvasCenterY;

      return {
        position: result,
        target: { position: centerPosition, type: 'canvas-center', axis }
      };
    }

    return null;
  }

  /** Checks if left edge, right edge, or center is within snap threshold of the horizontal centerline. */
  _checkCanvasCenterlineHorizontal(left, width) {
    const elementLeftEdge = left - SnapHelper.VISUAL_BOUNDARY_OFFSET;
    const elementRightEdge = left + width + SnapHelper.VISUAL_BOUNDARY_OFFSET;
    const elementCenterX = left + width / 2;

    if (Math.abs(elementLeftEdge - this.canvasCenterX) < SnapHelper.SNAP_THRESHOLD) {
      return this.canvasCenterX + SnapHelper.VISUAL_BOUNDARY_OFFSET;
    } else if (Math.abs(elementRightEdge - this.canvasCenterX) < SnapHelper.SNAP_THRESHOLD) {
      return this.canvasCenterX - SnapHelper.VISUAL_BOUNDARY_OFFSET - width;
    } else if (Math.abs(elementCenterX - this.canvasCenterX) < SnapHelper.SNAP_THRESHOLD) {
      return this.canvasCenterX - width / 2;
    }

    return null;
  }

  /** Checks if top edge, bottom edge, or center is within snap threshold of the vertical centerline. */
  _checkCanvasCenterlineVertical(top, height) {
    const elementTop = top - SnapHelper.VISUAL_BOUNDARY_OFFSET;
    const elementBottom = top + height + SnapHelper.VISUAL_BOUNDARY_OFFSET;
    const elementCenterY = top + height / 2;

    if (Math.abs(elementTop - this.canvasCenterY) < SnapHelper.SNAP_THRESHOLD) {
      return this.canvasCenterY + SnapHelper.VISUAL_BOUNDARY_OFFSET;
    } else if (Math.abs(elementBottom - this.canvasCenterY) < SnapHelper.SNAP_THRESHOLD) {
      return this.canvasCenterY - SnapHelper.VISUAL_BOUNDARY_OFFSET - height;
    } else if (Math.abs(elementCenterY - this.canvasCenterY) < SnapHelper.SNAP_THRESHOLD) {
      return this.canvasCenterY - height / 2;
    }

    return null;
  }
}
